// Pure utilization stats for the Signals catalog page. DB-free so the math is
// unit-tested without a database (mirrors the lib-pure / api-DB split used by
// intensity-suggestion.ts). Every `signal` row is one raise; a row keeps its
// terminal status, so status counts double as lifetime outcome counts and
// (open + confirmed + dismissed + resolvedBySystem) === totalRaised.
import { type signalStatus, signalType } from "@/db/schema/enums";

export const SIGNAL_STATS_WINDOWS = [30, 90] as const;
export type SignalStatsWindow = (typeof SIGNAL_STATS_WINDOWS)[number];

export type SignalStatType = (typeof signalType.enumValues)[number];
export type SignalStatStatus = (typeof signalStatus.enumValues)[number];

export interface SignalStatRow {
  type: SignalStatType;
  status: SignalStatStatus;
  raisedAt: Date;
  resolvedAt: Date | null;
}

export interface SignalTypeStats {
  type: SignalStatType;
  open: number;
  confirmed: number;
  dismissed: number;
  resolvedBySystem: number;
  totalRaised: number;
  raisedInWindow: number;
  resolvedInWindow: number;
  totalResolved: number;
  confirmedRate: number;
  dismissedRate: number;
  autoResolvedRate: number;
}

export interface SignalCatalogStats {
  window: SignalStatsWindow;
  generatedAt: string;
  perType: SignalTypeStats[];
  summary: { totalOpen: number; totalRaised: number; totalResolvedInWindow: number };
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function aggregateSignalStats(
  rows: SignalStatRow[],
  now: number,
  window: SignalStatsWindow,
): SignalCatalogStats {
  const cutoff = now - window * DAY_MS;

  const perType: SignalTypeStats[] = signalType.enumValues.map((type) => {
    const ofType = rows.filter((r) => r.type === type);
    const open = ofType.filter((r) => r.status === "open").length;
    const confirmed = ofType.filter((r) => r.status === "confirmed").length;
    const dismissed = ofType.filter((r) => r.status === "dismissed").length;
    const resolvedBySystem = ofType.filter((r) => r.status === "resolved_by_system").length;
    const totalResolved = confirmed + dismissed + resolvedBySystem;
    const raisedInWindow = ofType.filter((r) => r.raisedAt.getTime() >= cutoff).length;
    const resolvedInWindow = ofType.filter(
      (r) => r.resolvedAt !== null && r.resolvedAt.getTime() >= cutoff,
    ).length;
    const rate = (n: number): number => (totalResolved === 0 ? 0 : n / totalResolved);
    return {
      type,
      open,
      confirmed,
      dismissed,
      resolvedBySystem,
      totalRaised: ofType.length,
      raisedInWindow,
      resolvedInWindow,
      totalResolved,
      confirmedRate: rate(confirmed),
      dismissedRate: rate(dismissed),
      autoResolvedRate: rate(resolvedBySystem),
    };
  });

  return {
    window,
    generatedAt: new Date(now).toISOString(),
    perType,
    summary: {
      totalOpen: perType.reduce((s, t) => s + t.open, 0),
      totalRaised: perType.reduce((s, t) => s + t.totalRaised, 0),
      totalResolvedInWindow: perType.reduce((s, t) => s + t.resolvedInWindow, 0),
    },
  };
}
